import React, {forwardRef} from 'react';
import type {ButtonProps} from 'semantic-ui-react';
import classNames from 'classnames';

export interface IButtonProps extends ButtonProps {
}

type ButtonComponent = React.ForwardRefExoticComponent<IButtonProps & React.RefAttributes<HTMLElement>> & {
    Group: React.FC<React.HTMLAttributes<HTMLDivElement>>;
}

const Button = forwardRef<HTMLElement, IButtonProps>((props, ref) => {
    const {
        children,
        className,
        disabled,
        loading,
        content,
        icon,
        color,
        floated,
        basic,
        circular,
        fluid,
        inverted,
        primary,
        secondary,
        positive,
        negative,
        compact,
        toggle,
        active,
        as,
        href,
        target,
        rel,
        type,
        onClick,
        ...rest
    } = props;

    const isLink = as === 'a' || !!href;
    const classes = classNames(
        'ui',
        color,
        floated,
        {
            basic,
            circular,
            fluid,
            inverted,
            primary,
            secondary,
            positive,
            negative,
            compact,
            toggle,
            active,
            loading,
            disabled,
            icon: !!icon && !children && !content,
        },
        'button',
        'dvn__btn',
        className
    );

    const buttonContent = <>
        {typeof icon === 'string' && <i aria-hidden="true" className={`${icon} icon`}/>}
        {children ?? content}
    </>;

    if (isLink) {
        return <a
            {...rest as React.AnchorHTMLAttributes<HTMLAnchorElement>}
            ref={ref as React.Ref<HTMLAnchorElement>}
            className={classes}
            href={disabled ? undefined : href}
            target={target}
            rel={rel}
            aria-disabled={disabled}
            onClick={(event) => {
                if (disabled || loading) {
                    event.preventDefault();
                    return;
                }

                onClick?.(event as any, props);
            }}
        >
            {buttonContent}
        </a>
    }

    return <button
        {...rest as React.ButtonHTMLAttributes<HTMLButtonElement>}
        ref={ref as React.Ref<HTMLButtonElement>}
        className={classes}
        disabled={disabled || loading}
        type={type ?? 'button'}
        onClick={(event) => onClick?.(event as any, props)}
    >
        {buttonContent}
    </button>
}) as ButtonComponent;

Button.Group = ({children, className, ...rest}) => (
    <div {...rest} className={classNames('ui buttons', className)}>
        {children}
    </div>
);

export default Button;
